/**
 * BrandKitForm — form for the four MVP sections (Colors / Typography /
 * Logo & favicon / Footer & sender). Submits via RFC 7396 merge-patch
 * so blank inputs delete the field rather than stomp it with "".
 *
 * Hex validation is client-side AND server-side; the server is
 * authoritative but client-side keeps the user from round-tripping just
 * for typos.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { cn } from "../../lib/cn";
import type { BrandKit, BrandKitData, BrandKitPatch } from "../../lib/brandKits";

const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

type Tab = "colors" | "typography" | "logo" | "footer";

interface Props {
  kit: BrandKit;
  isSaving: boolean;
  saveError: string | null;
  onSave: (patch: BrandKitPatch) => Promise<void>;
}

interface FormState {
  name: string;
  primary: string;
  primary_contrast: string;
  background: string;
  surface: string;
  text_primary: string;
  text_secondary: string;
  border: string;
  accent: string;
  link: string;
  body_family: string;
  body_weight: string;
  heading_family: string;
  heading_weight: string;
  font_size_base: string;
  logo_url: string;
  logo_dark_url: string;
  logo_alt: string;
  logo_width: string;
  favicon_url: string;
  company_name: string;
  address: string;
  unsubscribe_text: string;
  legal_text: string;
  sender_name: string;
  sender_email: string;
  reply_to: string;
}

function fromKit(kit: BrandKit): FormState {
  const d = kit.data ?? {};
  const c = d.colors ?? {};
  const t = d.typography ?? {};
  const l = d.logo ?? {};
  const f = d.footer ?? {};
  const s = d.sender ?? {};
  const num = (n: number | undefined): string =>
    n === undefined ? "" : String(n);
  return {
    name: kit.name ?? "",
    primary: c.primary ?? "",
    primary_contrast: c.primary_contrast ?? "",
    background: c.background ?? "",
    surface: c.surface ?? "",
    text_primary: c.text_primary ?? "",
    text_secondary: c.text_secondary ?? "",
    border: c.border ?? "",
    accent: c.accent ?? "",
    link: c.link ?? "",
    body_family: t.body_font?.family ?? "",
    body_weight: num(t.body_font?.weight),
    heading_family: t.heading_font?.family ?? "",
    heading_weight: num(t.heading_font?.weight),
    font_size_base: num(t.font_size_base),
    logo_url: l.url ?? "",
    logo_dark_url: l.url_dark ?? "",
    logo_alt: l.alt ?? "",
    logo_width: num(l.width),
    favicon_url: d.favicon_url ?? "",
    company_name: f.company_name ?? "",
    address: f.address ?? "",
    unsubscribe_text: f.unsubscribe_text ?? "",
    legal_text: f.legal_text ?? "",
    sender_name: s.name ?? "",
    sender_email: s.email ?? "",
    reply_to: s.reply_to ?? "",
  };
}

/**
 * Convert blank → null (RFC 7396 delete) and pass-through trimmed
 * value otherwise. Numbers parse to int, blank → null.
 */
function strOrDel(v: string): string | null {
  return v.trim() === "" ? null : v.trim();
}
function numOrDel(v: string): number | null {
  const t = v.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function buildPatch(state: FormState, original: BrandKit): BrandKitPatch {
  // Only emit values that changed — keeps the patch focused, helps the
  // server-side audit log stay readable.
  const orig = fromKit(original);
  const colorPatch: Record<string, string | null> = {};
  const colorKeys = [
    "primary",
    "primary_contrast",
    "background",
    "surface",
    "text_primary",
    "text_secondary",
    "border",
    "accent",
    "link",
  ] as const;
  for (const k of colorKeys) {
    if (state[k] !== orig[k]) colorPatch[k] = strOrDel(state[k]);
  }

  const typography: Record<string, unknown> = {};
  if (state.body_family !== orig.body_family || state.body_weight !== orig.body_weight) {
    typography["body_font"] = {
      family: strOrDel(state.body_family),
      weight: numOrDel(state.body_weight),
    };
  }
  if (state.heading_family !== orig.heading_family || state.heading_weight !== orig.heading_weight) {
    typography["heading_font"] = {
      family: strOrDel(state.heading_family),
      weight: numOrDel(state.heading_weight),
    };
  }
  if (state.font_size_base !== orig.font_size_base) {
    typography["font_size_base"] = numOrDel(state.font_size_base);
  }

  const logo: Record<string, unknown> = {};
  if (state.logo_url !== orig.logo_url) logo["url"] = strOrDel(state.logo_url);
  if (state.logo_dark_url !== orig.logo_dark_url) logo["url_dark"] = strOrDel(state.logo_dark_url);
  if (state.logo_alt !== orig.logo_alt) logo["alt"] = strOrDel(state.logo_alt);
  if (state.logo_width !== orig.logo_width) logo["width"] = numOrDel(state.logo_width);

  const footer: Record<string, unknown> = {};
  const fooKeys = ["company_name", "address", "unsubscribe_text", "legal_text"] as const;
  for (const k of fooKeys) {
    if (state[k] !== orig[k]) footer[k] = strOrDel(state[k]);
  }

  const sender: Record<string, unknown> = {};
  if (state.sender_name !== orig.sender_name) sender["name"] = strOrDel(state.sender_name);
  if (state.sender_email !== orig.sender_email) sender["email"] = strOrDel(state.sender_email);
  if (state.reply_to !== orig.reply_to) sender["reply_to"] = strOrDel(state.reply_to);

  const data: Record<string, unknown> = {};
  if (Object.keys(colorPatch).length > 0) data["colors"] = colorPatch;
  if (Object.keys(typography).length > 0) data["typography"] = typography;
  if (Object.keys(logo).length > 0) data["logo"] = logo;
  if (state.favicon_url !== orig.favicon_url) data["favicon_url"] = strOrDel(state.favicon_url);
  if (Object.keys(footer).length > 0) data["footer"] = footer;
  if (Object.keys(sender).length > 0) data["sender"] = sender;

  const patch: BrandKitPatch = {};
  if (state.name !== orig.name && state.name.trim() !== "") {
    patch.name = state.name.trim();
  }
  if (Object.keys(data).length > 0) patch.data = data as BrandKitData;
  return patch;
}

function validateColors(state: FormState, t: (k: string) => string): Record<string, string> {
  const errors: Record<string, string> = {};
  const colorKeys = [
    "primary",
    "primary_contrast",
    "background",
    "surface",
    "text_primary",
    "text_secondary",
    "border",
    "accent",
    "link",
  ] as const;
  for (const k of colorKeys) {
    const v = state[k].trim();
    if (v !== "" && !HEX_RE.test(v)) {
      errors[k] = t("brand_kit.errors.invalid_hex");
    }
  }
  return errors;
}

const TABS: Array<{ id: Tab; key: string }> = [
  { id: "colors", key: "brand_kit.tabs.colors" },
  { id: "typography", key: "brand_kit.tabs.typography" },
  { id: "logo", key: "brand_kit.tabs.logo" },
  { id: "footer", key: "brand_kit.tabs.footer" },
];

export function BrandKitForm({ kit, isSaving, saveError, onSave }: Props): JSX.Element {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("colors");
  const [state, setState] = useState<FormState>(() => fromKit(kit));
  const [errors, setErrors] = useState<Record<string, string>>({});

  // When the loaded kit changes (e.g. after a successful save), reset
  // the form state — this keeps the dirty-tracking honest.
  useEffect(() => {
    setState(fromKit(kit));
    setErrors({});
  }, [kit]);

  function setField<K extends keyof FormState>(field: K, value: FormState[K]): void {
    setState((s) => ({ ...s, [field]: value }));
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const errs = validateColors(state, t);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const patch = buildPatch(state, kit);
    if (Object.keys(patch).length === 0) return;
    await onSave(patch);
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="space-y-6">
      <Input
        label={t("brand_kit.fields.name")}
        value={state.name}
        onChange={(e) => setField("name", e.target.value)}
        maxLength={120}
      />

      <div className="border-b border-neutral-200">
        <nav className="-mb-px flex gap-4">
          {TABS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => setTab(entry.id)}
              className={cn(
                "border-b-2 px-1 pb-3 text-sm font-medium transition-colors",
                tab === entry.id
                  ? "border-primary-600 text-primary-700"
                  : "border-transparent text-neutral-500 hover:text-neutral-700",
              )}
            >
              {t(entry.key)}
            </button>
          ))}
        </nav>
      </div>

      {tab === "colors" && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {(
            [
              "primary",
              "primary_contrast",
              "background",
              "surface",
              "text_primary",
              "text_secondary",
              "border",
              "accent",
              "link",
            ] as const
          ).map((key) => (
            <ColorField
              key={key}
              id={key}
              label={t(`brand_kit.fields.${key}`)}
              value={state[key]}
              onChange={(v) => setField(key, v)}
              error={errors[key]}
            />
          ))}
        </div>
      )}

      {tab === "typography" && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            label={`${t("brand_kit.fields.body_font")} — ${t("brand_kit.fields.font_family")}`}
            value={state.body_family}
            onChange={(e) => setField("body_family", e.target.value)}
          />
          <Input
            label={`${t("brand_kit.fields.body_font")} — ${t("brand_kit.fields.font_weight")}`}
            type="number"
            min={100}
            max={900}
            step={100}
            value={state.body_weight}
            onChange={(e) => setField("body_weight", e.target.value)}
          />
          <Input
            label={`${t("brand_kit.fields.heading_font")} — ${t("brand_kit.fields.font_family")}`}
            value={state.heading_family}
            onChange={(e) => setField("heading_family", e.target.value)}
          />
          <Input
            label={`${t("brand_kit.fields.heading_font")} — ${t("brand_kit.fields.font_weight")}`}
            type="number"
            min={100}
            max={900}
            step={100}
            value={state.heading_weight}
            onChange={(e) => setField("heading_weight", e.target.value)}
          />
          <Input
            label={t("brand_kit.fields.font_size_base")}
            type="number"
            min={10}
            max={32}
            value={state.font_size_base}
            onChange={(e) => setField("font_size_base", e.target.value)}
          />
        </div>
      )}

      {tab === "logo" && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            label={t("brand_kit.fields.logo_url")}
            value={state.logo_url}
            onChange={(e) => setField("logo_url", e.target.value)}
            placeholder="https://…"
          />
          <Input
            label={t("brand_kit.fields.logo_dark_url")}
            value={state.logo_dark_url}
            onChange={(e) => setField("logo_dark_url", e.target.value)}
            placeholder="https://…"
          />
          <Input
            label={t("brand_kit.fields.logo_alt")}
            value={state.logo_alt}
            onChange={(e) => setField("logo_alt", e.target.value)}
          />
          <Input
            label={t("brand_kit.fields.logo_width")}
            type="number"
            min={1}
            value={state.logo_width}
            onChange={(e) => setField("logo_width", e.target.value)}
          />
          <Input
            label={t("brand_kit.fields.favicon_url")}
            value={state.favicon_url}
            onChange={(e) => setField("favicon_url", e.target.value)}
            placeholder="https://…"
            containerClassName="md:col-span-2"
          />
        </div>
      )}

      {tab === "footer" && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            label={t("brand_kit.fields.company_name")}
            value={state.company_name}
            onChange={(e) => setField("company_name", e.target.value)}
          />
          <Input
            label={t("brand_kit.fields.address")}
            value={state.address}
            onChange={(e) => setField("address", e.target.value)}
          />
          <Input
            label={t("brand_kit.fields.unsubscribe_text")}
            value={state.unsubscribe_text}
            onChange={(e) => setField("unsubscribe_text", e.target.value)}
            containerClassName="md:col-span-2"
          />
          <Input
            label={t("brand_kit.fields.legal_text")}
            value={state.legal_text}
            onChange={(e) => setField("legal_text", e.target.value)}
            containerClassName="md:col-span-2"
          />
          <Input
            label={t("brand_kit.fields.sender_name")}
            value={state.sender_name}
            onChange={(e) => setField("sender_name", e.target.value)}
          />
          <Input
            label={t("brand_kit.fields.sender_email")}
            type="email"
            value={state.sender_email}
            onChange={(e) => setField("sender_email", e.target.value)}
          />
          <Input
            label={t("brand_kit.fields.reply_to")}
            type="email"
            value={state.reply_to}
            onChange={(e) => setField("reply_to", e.target.value)}
            containerClassName="md:col-span-2"
          />
        </div>
      )}

      <div className="flex items-center gap-3 border-t border-neutral-200 pt-4">
        <Button type="submit" disabled={isSaving}>
          {isSaving ? t("brand_kit.actions.saving") : t("brand_kit.actions.save")}
        </Button>
        {saveError !== null && (
          <span role="alert" className="text-sm text-error-600">
            {saveError}
          </span>
        )}
      </div>
    </form>
  );
}

interface ColorFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error: string | undefined;
}

function ColorField({ id, label, value, onChange, error }: ColorFieldProps): JSX.Element {
  // Native <input type="color"> requires #RRGGBB; if the user typed a
  // valid hex we mirror it into the swatch, otherwise the swatch stays
  // black-by-default. Color picker (T3.3) replaces this Phase 3.
  const swatchValue = HEX_RE.test(value) && value.length === 7 ? value : "#000000";
  return (
    <div className="flex items-end gap-2">
      <Input
        id={id}
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#1f9eff"
        error={error}
        containerClassName="flex-1"
      />
      <input
        aria-label={label}
        type="color"
        value={swatchValue}
        onChange={(e) => onChange(e.target.value)}
        className="mb-[1px] h-10 w-10 cursor-pointer rounded-md border border-neutral-300 bg-white p-1"
      />
    </div>
  );
}
